/**
 * API Client for Endure Health API
 * Handles all communication with the Fastify backend
 */

interface ApiResponse<T = any> {
  data?: T;
  error?: string;
  message?: string;
}

interface User {
  id: string;
  email: string;
  name?: string;
  username?: string;
  image?: string;
  createdAt: string;
}

interface LoginResponse {
  user: User;
  token: string;
}

interface Post {
  id: string;
  content: string;
  authorId: string;
  author: {
    id: string;
    name?: string;
    username?: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface CalendarEvent {
  id: string;
  name: string;
  description: string;
  startDate: Date;
  endDate: Date;
  location: string;
  latitude: number;
  longitude: number;
  createdAt: Date;
  updatedAt: Date;
  calendarId: number | null;
}

interface Calendar {
  id: string;
  name: string;
  events: CalendarEvent[];
}

interface Message {
  id: string;
  content: string;
  senderId: string;
  conversationId: string;
  createdAt: string;
}

interface Metric {
  id: string;
  name: string;
  value: number;
  unit: string;
  timestamp: Date;
}

interface Challenge {
  id: string;
  title: string;
  description: string;
  createdAt: string;
}

interface Meal {
  id: string;
  name: string;
  calories: number;
  createdAt: string;
}

interface WearableConnection {
  id: string;
  provider: string;
  status: string;
  connectedAt: string;
}

interface Event {
  id: string;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  location: string;
}

interface Limitation {
  id: string;
  type: string;
  description: string;
  createdAt: string;
}

class ApiClient {
  private baseURL: string;
  private token: string | null = null;

  constructor() {
    this.baseURL = process?.env?.NEXT_PUBLIC_API_URL || 'http://localhost:3001';
    
    // Initialize token from localStorage if available
    if (typeof window !== 'undefined') {
      this.token = localStorage.getItem('auth_token');
    }
  }

  setToken(token: string) {
    this.token = token;
    if (typeof window !== 'undefined') {
      localStorage.setItem('auth_token', token);
    }
  }

  clearToken() {
    this.token = null;
    if (typeof window !== 'undefined') {
      localStorage.removeItem('auth_token');
    }
  }

  private async request<T = any>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    
    const headers: HeadersInit = {
      ...options.headers,
    };

    // Only set Content-Type if there's a body
    if (options.body) {
      (headers as any)['Content-Type'] = 'application/json';
    }

    // Add authorization header if token exists
    if (this.token) {
      (headers as any)['Authorization'] = `Bearer ${this.token}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API Error [${endpoint}]:`, error);
      throw error;
    }
  }

  // Authentication methods
  async login(email: string, password: string): Promise<LoginResponse> {
    const response = await this.request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    
    if (response.token) {
      this.setToken(response.token);
    }
    
    return response;
  }

  async register(email: string, password: string, name?: string, username?: string): Promise<LoginResponse> {
    const response = await this.request<LoginResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, name, username }),
    });
    
    if (response.token) {
      this.setToken(response.token);
    }
    
    return response;
  }

  async getMe(): Promise<{ user: User }> {
    return this.request<{ user: User }>('/auth/me');
  }

  async refreshToken(): Promise<{ token: string }> {
    const response = await this.request<{ token: string }>('/auth/refresh', {
      method: 'POST',
    });
    
    if (response.token) {
      this.setToken(response.token);
    }
    
    return response;
  }

  // User methods
  async getUser(id: string): Promise<{ user: User }> {
    return this.request<{ user: User }>(`/users/${id}`);
  }

  async updateProfile(data: Partial<User>): Promise<{ user: User }> {
    return this.request<{ user: User }>('/users/me', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async searchUsers(query: string): Promise<{ users: User[] }> {
    return this.request<{ users: User[] }>(`/users/search?q=${encodeURIComponent(query)}`);
  }

  async followUser(userId: string): Promise<void> {
    await this.request('/users/follow', {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  }

  async unfollowUser(userId: string): Promise<void> {
    await this.request('/users/unfollow', {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  }

  async findUser(params: { id?: string; username?: string }): Promise<{ user: User }> {
    const queryParams = new URLSearchParams();
    if (params.id) queryParams.append('id', params.id);
    if (params.username) queryParams.append('username', params.username);
    
    return this.request<{ user: User }>(`/users/find?${queryParams}`);
  }

  // Post methods
  async getPosts(): Promise<{ posts: Post[] }> {
    return this.request<{ posts: Post[] }>('/posts');
  }

  async getPost(id: string): Promise<{ post: Post }> {
    return this.request<{ post: Post }>(`/posts/${id}`);
  }

  async createPost(content: string): Promise<{ post: Post }> {
    return this.request<{ post: Post }>('/posts', {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  }

  async updatePost(id: string, content: string): Promise<{ post: Post }> {
    return this.request<{ post: Post }>(`/posts/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ content }),
    });
  }

  async deletePost(id: string): Promise<void> {
    await this.request(`/posts/${id}`, {
      method: 'DELETE',
    });
  }

  async likePost(id: string): Promise<void> {
    await this.request(`/posts/${id}/like`, {
      method: 'POST',
    });
  }

  async unlikePost(id: string): Promise<void> {
    await this.request(`/posts/${id}/like`, {
      method: 'DELETE',
    });
  }

  async bookmarkPost(id: string): Promise<void> {
    await this.request(`/posts/${id}/bookmark`, {
      method: 'POST',
    });
  }

  async repostPost(id: string): Promise<{ post: Post }> {
    return this.request<{ post: Post }>(`/posts/${id}/repost`, {
      method: 'POST',
    });
  }

  async unrepostPost(id: string): Promise<void> {
    await this.request(`/posts/${id}/repost`, {
      method: 'DELETE',
    });
  }

  async quotePost(id: string, content: string): Promise<{ post: Post }> {
    return this.request<{ post: Post }>(`/posts/${id}/quote`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  }

  async getBookmarkStatus(id: string): Promise<{ isBookmarked: boolean; bookmarkCount: number }> {
    return this.request<{ isBookmarked: boolean; bookmarkCount: number }>(`/posts/${id}/bookmarks/status`);
  }

  async getSavedPosts(): Promise<{ posts: Post[] }> {
    return this.request<{ posts: Post[] }>('/posts/saved');
  }

  // Message methods
  async getMessages(): Promise<{ messages: Message[] }> {
    return this.request<{ messages: Message[] }>('/messages');
  }

  async sendMessage(content: string, conversationId?: string): Promise<{ message: Message }> {
    return this.request<{ message: Message }>('/messages', {
      method: 'POST',
      body: JSON.stringify({ content, conversationId }),
    });
  }

  async getConversationMessages(conversationId: string): Promise<{ messages: Message[] }> {
    return this.request<{ messages: Message[] }>(`/messages/conversations/${conversationId}`);
  }

  // WebRTC methods
  async getTurnConfig(): Promise<{ iceServers: any[] }> {
    return this.request<{ iceServers: any[] }>('/webrtc/turn-config');
  }

  async checkWebRTCHealth(): Promise<{ status: string }> {
    return this.request<{ status: string }>('/webrtc/health');
  }

  // Exercise methods
  async getExercises(): Promise<{ exercises: any[] }> {
    return this.request<{ exercises: any[] }>('/exercise');
  }

  async analyzeExercise(data: any): Promise<{ analysis: any }> {
    return this.request<{ analysis: any }>('/exercise/analyze', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Meal methods
  async getMeals(): Promise<{ meals: Meal[] }> {
    return this.request<{ meals: Meal[] }>('/meals');
  }

  async analyzeMeal(data: any): Promise<{ analysis: any }> {
    return this.request<{ analysis: any }>('/meals/analyze', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Wearable methods
  async getWearableData(params?: any): Promise<{ data: Metric[] }> {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.request<{ data: Metric[] }>(`/wearables/data${queryString}`);
  }

  async connectWearable(data: any): Promise<{ connection: WearableConnection }> {
    return this.request<{ connection: WearableConnection }>('/wearables/connect', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async syncWearables(dataTypes?: string[]): Promise<{ sync: any }> {
    return this.request<{ sync: any }>('/wearables/sync', {
      method: 'POST',
      body: JSON.stringify({ dataTypes }),
    });
  }

  // Event methods
  async getEvents(): Promise<{ events: Event[] }> {
    return this.request<{ events: Event[] }>('/events');
  }

  async createEvent(eventData: any): Promise<{ event: Event }> {
    return this.request<{ event: Event }>('/events', {
      method: 'POST',
      body: JSON.stringify(eventData),
    });
  }

  async attendEvent(eventId: string, status: string): Promise<{ attendance: any }> {
    return this.request<{ attendance: any }>(`/events/${eventId}/attend`, {
      method: 'POST',
      body: JSON.stringify({ status }),
    });
  }

  // Community methods
  async getCommunities(): Promise<{ communities: any[] }> {
    return this.request<{ communities: any[] }>('/communities');
  }

  async joinCommunity(communityId: string): Promise<{ membership: any }> {
    return this.request<{ membership: any }>(`/communities/${communityId}/join`, {
      method: 'POST',
    });
  }

  async leaveCommunity(communityId: string): Promise<void> {
    await this.request(`/communities/${communityId}/leave`, {
      method: 'POST',
    });
  }

  // AI methods
  async chatWithAI(message: string, conversationId?: string): Promise<{ response: any }> {
    return this.request<{ response: any }>('/ai/chat', {
      method: 'POST',
      body: JSON.stringify({ message, conversationId }),
    });
  }

  async getAIConversations(): Promise<{ conversations: any[] }> {
    return this.request<{ conversations: any[] }>('/ai/conversations');
  }

  async queryRAG(query: string, sources?: string[]): Promise<{ ragResponse: any }> {
    return this.request<{ ragResponse: any }>('/ai/rag', {
      method: 'POST',
      body: JSON.stringify({ query, sources }),
    });
  }

  // Missing methods for complete migration
  async getUnreadMessageCount(): Promise<{ count: number }> {
    return this.request<{ count: number }>('/messages/unread/count');
  }

  async processPayment(data: any): Promise<{ success: boolean; transactionId: string }> {
    return this.request<{ success: boolean; transactionId: string }>('/marketplace/payment', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createChallenge(data: any): Promise<{ challenge: Challenge }> {
    return this.request<{ challenge: Challenge }>('/challenges', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getMealOverlay(data: any): Promise<{ overlay: any }> {
    return this.request<{ overlay: any }>('/meals/preview-overlay', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createMeal(data: any): Promise<{ meal: Meal }> {
    return this.request<{ meal: Meal }>('/meals', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }



  async syncWearableData(): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>('/wearables/sync', {
      method: 'POST',
    });
  }


  async createChatConversation(): Promise<{ conversation: any }> {
    return this.request<{ conversation: any }>('/ai/conversations', {
      method: 'POST',
    });
  }

  async uploadFile(file: File): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append('file', file);
    return this.request<{ url: string }>('/mobile/upload', {
      method: 'POST',
      body: formData,
    });
  }


  async createScheduledPost(data: any): Promise<{ post: Post }> {
    return this.request<{ post: Post }>('/posts/scheduled', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createLimitation(data: any): Promise<{ limitation: Limitation }> {
    return this.request<{ limitation: Limitation }>('/limitations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getBookmarks(): Promise<{ posts: Post[] }> {
    return this.request<{ posts: Post[] }>('/bookmarks');
  }

  async addBookmark(postId: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/posts/${postId}/bookmark`, {
      method: 'POST',
    });
  }

  async removeBookmark(postId: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/posts/${postId}/bookmark`, {
      method: 'DELETE',
    });
  }

  // Notification methods
  async getNotifications(params?: { unread?: boolean; type?: string; limit?: number }): Promise<{ notifications: any[]; unreadCount: number }> {
    const queryString = params ? `?${new URLSearchParams(params as any).toString()}` : '';
    return this.request<{ notifications: any[]; unreadCount: number }>(`/notifications${queryString}`);
  }

  async markNotificationsRead(notificationIds: string[]): Promise<void> {
    await this.request('/notifications/mark-read', {
      method: 'POST',
      body: JSON.stringify({ notificationIds }),
    });
  }

  async markAllNotificationsRead(): Promise<void> {
    await this.request('/notifications/mark-all-read', {
      method: 'POST',
    });
  }

  async deleteNotification(id: string): Promise<void> {
    await this.request(`/notifications/${id}`, {
      method: 'DELETE',
    });
  }

  async getNotificationPreferences(): Promise<{ preferences: any }> {
    return this.request<{ preferences: any }>('/notifications/preferences');
  }

  async updateNotificationPreferences(preferences: any): Promise<{ preferences: any }> {
    return this.request<{ preferences: any }>('/notifications/preferences', {
      method: 'PUT',
      body: JSON.stringify(preferences),
    });
  }

  // Settings methods
  async getSettings(): Promise<{ settings: any }> {
    return this.request<{ settings: any }>('/settings');
  }

  async updateSettings(settings: any): Promise<{ settings: any }> {
    return this.request<{ settings: any }>('/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  async getPrivacySettings(): Promise<{ privacy: any }> {
    return this.request<{ privacy: any }>('/settings/privacy');
  }

  async updatePrivacySettings(privacy: any): Promise<{ privacy: any }> {
    return this.request<{ privacy: any }>('/settings/privacy', {
      method: 'PUT',
      body: JSON.stringify(privacy),
    });
  }

  async getAccountSettings(): Promise<{ account: any }> {
    return this.request<{ account: any }>('/settings/account');
  }

  async exportUserData(): Promise<{ exportRequest: any }> {
    return this.request<{ exportRequest: any }>('/settings/export', {
      method: 'POST',
    });
  }

  async deleteAccount(password: string, reason?: string): Promise<{ deletion: any }> {
    return this.request<{ deletion: any }>('/settings/account', {
      method: 'DELETE',
      body: JSON.stringify({ password, reason }),
    });
  }

  // Admin methods
  async getUsers(params?: { page?: number; limit?: number; status?: string; role?: string }): Promise<{ users: any[]; pagination: any }> {
    const queryString = params ? `?${new URLSearchParams(params as any).toString()}` : '';
    return this.request<{ users: any[]; pagination: any }>(`/admin/users${queryString}`);
  }

  async getAdminUser(id: string): Promise<{ user: any }> {
    return this.request<{ user: any }>(`/admin/users/${id}`);
  }

  async updateAdminUser(id: string, data: any): Promise<{ user: any }> {
    return this.request<{ user: any }>(`/admin/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async suspendUser(id: string, reason: string, duration?: number, permanent?: boolean): Promise<{ suspension: any }> {
    return this.request<{ suspension: any }>(`/admin/users/${id}/suspend`, {
      method: 'POST',
      body: JSON.stringify({ reason, duration, permanent }),
    });
  }

  async getAdminStats(): Promise<{ stats: any }> {
    return this.request<{ stats: any }>('/admin/stats');
  }

  // Calendar methods
  async getCalendarEvents(params?: { startDate?: string; endDate?: string; type?: string }): Promise<{ events: CalendarEvent[] }> {
    const queryString = params ? `?${new URLSearchParams(params as any).toString()}` : '';
    return this.request<{ events: CalendarEvent[] }>(`/calendar/events${queryString}`);
  }

  async createCalendarEvent(eventData: any): Promise<{ event: CalendarEvent }> {
    return this.request<{ event: CalendarEvent }>('/calendar/events', {
      method: 'POST',
      body: JSON.stringify(eventData),
    });
  }

  async updateCalendarEvent(id: string, eventData: any): Promise<{ event: CalendarEvent }> {
    return this.request<{ event: CalendarEvent }>(`/calendar/events/${id}`, {
      method: 'PUT',
      body: JSON.stringify(eventData),
    });
  }

  async deleteCalendarEvent(id: string): Promise<void> {
    await this.request(`/calendar/events/${id}`, {
      method: 'DELETE',
    });
  }

  async getCalendarSummary(period?: string): Promise<{ summary: any }> {
    const queryString = period ? `?period=${period}` : '';
    return this.request<{ summary: any }>(`/calendar/summary${queryString}`);
  }

  // Poll methods
  async getPoll(id: string): Promise<{ poll: any }> {
    return this.request<{ poll: any }>(`/polls/${id}`);
  }

  async createPoll(pollData: any): Promise<{ poll: any }> {
    return this.request<{ poll: any }>('/polls', {
      method: 'POST',
      body: JSON.stringify(pollData),
    });
  }

  async voteOnPoll(id: string, optionIndex: number): Promise<{ vote: any }> {
    return this.request<{ vote: any }>(`/polls/${id}/vote`, {
      method: 'POST',
      body: JSON.stringify({ optionIndex }),
    });
  }

  async getPollResults(id: string): Promise<{ results: any }> {
    return this.request<{ results: any }>(`/polls/${id}/results`);
  }

  async deletePoll(id: string): Promise<void> {
    await this.request(`/polls/${id}`, {
      method: 'DELETE',
    });
  }

  // Avatar methods
  async getAvatars(): Promise<{ avatars: any[] }> {
    const avatars = await this.request<any[]>('/user/avatars');
    return { avatars };
  }

  async createAvatar(avatarData: any): Promise<{ task_id: string; status: string; message: string }> {
    return this.request<{ task_id: string; status: string; message: string }>('/avatars', {
      method: 'POST',
      body: JSON.stringify(avatarData),
    });
  }

  async uploadAvatarImage(): Promise<{ upload: any }> {
    return this.request<{ upload: any }>('/avatars/upload', {
      method: 'POST',
    });
  }

  async getAvatarRig(id: string): Promise<{ rigData: any }> {
    return this.request<{ rigData: any }>(`/avatars/${id}/rig`);
  }

  async updateRigStatus(avatarId: string, status: string): Promise<{ rigStatus: any }> {
    return this.request<{ rigStatus: any }>('/avatars/rig-status', {
      method: 'POST',
      body: JSON.stringify({ avatarId, status }),
    });
  }

  async setDefaultAvatar(avatarId: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>('/user/avatars', {
      method: 'PUT',
      body: JSON.stringify({ avatarId }),
    });
  }

  async deleteAvatar(avatarId: string): Promise<void> {
    await this.request(`/avatars/${avatarId}`, {
      method: 'DELETE',
    });
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

// Export types for use in components
export type {
  User,
  Post,
  LoginResponse,
  CalendarEvent,
  Calendar,
  Message,
  Metric,
  Challenge,
  Meal,
  WearableConnection,
  Event,
  Limitation
};